import { FC, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { useApiPost } from "../../../../hooks";
import { isAPITotallyComplete } from "../../../../utilities/apiFunctions";
import { Font } from "../../../TrueUI";
import { BindInstructionsInfoAtom } from "./PolicyQuoteBindInstructionsAtoms";
import { PolicyQuoteBindInstructionsTableProps } from "./PolicyQuoteBindInstructionsTypes";
import {
  depositTypes,
  getCurrentTotalPremium,
  getTableInfoByBillByLocation,
} from "./PolicyQuoteBindInstructionsUtils";
import { PaymentScheduleTableAndInvoicesDto } from "../../../../dtos/payment-schedule-table-and-invoices-dto";
import { PolicyTypeEnum } from "../../../../dtos/policy-type-enum";
import { PolicyScheduleTableRequestDto } from "../../../../dtos/policy-schedule-table-request-dto";
import PolicyQuoteBindInstructionsMultiTable from "./PolicyQuoteBindInstructionsMultiTable";
import PolicyQuoteBindInstructionsTable from "./PolicyQuoteBindInstructionsTable";
import { conditionHasValue } from "../../../../utilities/conditionalSupportFunctions";
import "./PolicyQuoteBindInstructionsStyles.module.css";
import DialogConfirmation from "../../../TrueUI/Dialogs/DialogConfirmation";
import { isDateAfterDate } from "../../../../utilities/dateFunctions";

const PolicyQuoteBindInstructionsTables: FC<
  PolicyQuoteBindInstructionsTableProps
> = ({ readOnly, insuredId }) => {
  const [bindInstructionsInfo, setBindInstructionsInfo] = useRecoilState(
    BindInstructionsInfoAtom
  );
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const showScheduleTable =
    conditionHasValue(bindInstructionsInfo?.billedDepositAmount) &&
    bindInstructionsInfo?.billingMethod !== depositTypes.REPORTING;

  const [showTable, setShowTable] = useState<boolean>(showScheduleTable);

  const tableURL = "api/QuotePolicy/GetInvoicesAndScheduleTableForInitialQuote";

  const reviewIfRegenerate = () => {
    if (bindInstructionsInfo?.shouldReGenerateInvoices !== true) {
      const totalValueFromInvoices = bindInstructionsInfo?.invoices?.reduce(
        (prev, curr) => {
          return prev + curr.invoiceTotal;
        },
        0
      );

      const totalPremium = getCurrentTotalPremium(bindInstructionsInfo);

      const total = totalPremium + (bindInstructionsInfo?.billedFees ?? 0);

      if (
        bindInstructionsInfo?.readOnly === false &&
        totalValueFromInvoices !== total
      ) {
        return true;
      }
      return false;
    }
    return true;
  };

  const getPaymentScheduleParams = (): PolicyScheduleTableRequestDto => {
    return {
      shouldReGenerateInvoices: reviewIfRegenerate(),
      effectiveDate: bindInstructionsInfo?.effectiveDate ?? new Date(),
      billedPremiumAmount: bindInstructionsInfo?.billedPremium ?? 0,
      bindDiscountAmount: bindInstructionsInfo?.bindDiscountAmount ?? 0,
      billedFeesAmount: bindInstructionsInfo?.billedFees ?? 0,
      billedDepositAmount: bindInstructionsInfo?.billedDepositAmount ?? 0,
      installmentPaymentAmount: bindInstructionsInfo?.installmentPayment ?? 0,
      depositRate: bindInstructionsInfo?.depositRate ?? 0,
      billingMethod: bindInstructionsInfo?.billingMethod ?? "",
      billByLocation: bindInstructionsInfo?.billByLocation ?? false,
      payPlan: bindInstructionsInfo?.payPlan ?? {},
      policyType: bindInstructionsInfo?.policyType ?? PolicyTypeEnum.NEW_POLICY,
      nonEarningAmount: bindInstructionsInfo?.nonEarningAmount ?? -0.1,
      states: bindInstructionsInfo?.states ?? [],
      addresses: bindInstructionsInfo?.addresses ?? null,
      invoices: bindInstructionsInfo?.invoices ?? null,
    };
  };

  const {
    responsePost: scheduleTableResponse,
    dispatchPost: scheduleTableDispatch,
  } = useApiPost<PaymentScheduleTableAndInvoicesDto>(
    tableURL,
    getPaymentScheduleParams()
  );

  useEffect(() => {
    if (bindInstructionsInfo?.dispatchTable) {
      setShowTable(false);
      scheduleTableDispatch();
      setBindInstructionsInfo({
        ...bindInstructionsInfo,
        dispatchTable: false,
      });
    }
  }, [bindInstructionsInfo?.dispatchTable]);

  useEffect(() => {
    if (isAPITotallyComplete(scheduleTableResponse)) {
      const invoices = scheduleTableResponse.axiosResponse?.data.invoices;
      setBindInstructionsInfo({
        ...bindInstructionsInfo,
        invoices: scheduleTableResponse.axiosResponse?.data.invoices,
        scheduleTableInfo: getTableInfoByBillByLocation(
          bindInstructionsInfo?.billByLocation,
          scheduleTableResponse?.axiosResponse?.data
        ),
        shouldReGenerateInvoices: false,
      });

      setShowTable(showScheduleTable);

      const areInvoicesCorrect = invoices?.every((i) =>
        isDateAfterDate(bindInstructionsInfo?.expirationDate, i.dueDate)
      );

      if (areInvoicesCorrect !== true && readOnly === false) {
        setIsDialogOpen(true);
      }
    }
  }, [scheduleTableResponse]);

  useEffect(() => {
    if (readOnly === true) {
      setBindInstructionsInfo({
        ...bindInstructionsInfo,
        shouldReGenerateInvoices: false,
        dispatchTable: true,
      });
    }
  }, [readOnly]);

  return (
    <div className={"table_schedule_container"}>
      {showTable ? (
        <>
          <Font
            className={`payment-schedule-table-title${
              readOnly ? "" : "-compact"
            }`}
          >
            PAYMENT SCHEDULE
          </Font>

          <div className="payment_schedule_table">
            {bindInstructionsInfo?.billByLocation === true ? (
              <PolicyQuoteBindInstructionsMultiTable
                readOnly={readOnly}
                insuredId={insuredId}
                setIsDialogOpen={setIsDialogOpen}
              />
            ) : (
              <PolicyQuoteBindInstructionsTable
                readOnly={readOnly}
                insuredId={insuredId}
                setIsDialogOpen={setIsDialogOpen}
              />
            )}
          </div>
        </>
      ) : null}
      <DialogConfirmation
        dialogTitle="Invoice Date"
        open={isDialogOpen}
        optionYesOverrideLabel="OK"
        onCloseEvent={() => setIsDialogOpen(false)}
        onOptionYesEvent={() => setIsDialogOpen(false)}
        dialogDescriptionText="The payment plan selected will have invoices due after the expiration date of the policy.  Please review or change the payment plan."
      />
    </div>
  );
};

export default PolicyQuoteBindInstructionsTables;
